import { useState, useEffect, useRef } from 'react';
import { Check, Pencil, Plus, Minus, ChevronDown } from 'lucide-react';

export const PERSONAL_GOALS = [
  { id: 'thought_leadership', de: 'Thought Leadership', en: 'Thought leadership', descDe: 'Als Expertin/Experte für ein Thema wahrgenommen werden', descEn: 'Be seen as an expert on a topic' },
  { id: 'personal_brand', de: 'Personal Brand', en: 'Personal brand', descDe: 'Sichtbarkeit und Wiedererkennung gewinnen', descEn: 'Gain visibility and recognition' },
  { id: 'lead_generation', de: 'Leads generieren', en: 'Generate leads', descDe: 'Passende Kunden und Anfragen gewinnen', descEn: 'Win fitting clients and enquiries' },
  { id: 'career', de: 'Karriere', en: 'Career', descDe: 'Chancen und Reputation in der Branche', descEn: 'Opportunities and reputation in your industry' },
  { id: 'network', de: 'Netzwerk & Community', en: 'Network & community', descDe: 'Austausch mit Peers und Multiplikatoren', descEn: 'Exchange with peers and multipliers' },
];

const clamp2 = { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' };

// Textfeld, das mit dem Inhalt wächst — langer Text wird umgebrochen statt abgeschnitten.
export const AutoGrow = ({ value, onChange, className = '', ...rest }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    // Ausgeblendet (z. B. Reiter nicht aktiv) ist die Höhe 0: dann nichts setzen und beim Einblenden neu berechnen
    const fit = () => { el.style.height = 'auto'; if (el.scrollHeight) el.style.height = `${el.scrollHeight}px`; };
    fit();
    if (typeof ResizeObserver === 'undefined') return undefined;
    // Nur auf Breitenänderungen reagieren (Einblenden, Fenstergröße), nie auf die eigene Höhe: sonst entsteht eine Beobachter-Schleife
    let lastWidth = el.offsetWidth;
    let raf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => { if (el.offsetWidth !== lastWidth) { lastWidth = el.offsetWidth; fit(); } });
    });
    ro.observe(el);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [value]);
  return (
    <textarea
      ref={ref} rows={1} value={value || ''} onChange={onChange}
      className={`w-full resize-none overflow-hidden rounded-lg border px-3 py-2 leading-snug focus:outline-none focus:ring-1 focus:ring-[#00D9C8] ${className}`}
      {...rest}
    />
  );
};

/**
 * Einklappbare Karte: zugeklappt nur Titel + kurze Zusammenfassung, aufgeklappt der Inhalt.
 * `right` ist ein Platz für Aktionen (z. B. Speichern), er löst das Auf-/Zuklappen nicht aus.
 */
export const Collapsible = ({ title, summary, defaultOpen = false, right, children, testId, tone = 'card' }) => {
  const [open, setOpen] = useState(defaultOpen);
  const base = tone === 'card' ? 'bg-[#0F1D32] border border-[#1A2B42] rounded-xl' : 'bg-[#1A2B42] rounded-lg';
  return (
    <div className={base} data-testid={testId}>
      <div className="flex items-center gap-2 px-3.5 py-3">
        <button type="button" onClick={() => setOpen(o => !o)} className="flex-1 min-w-0 flex items-center gap-2 text-left" aria-expanded={open} data-testid={testId ? `${testId}-toggle` : undefined}>
          <ChevronDown className={`w-4 h-4 flex-shrink-0 text-[#5A6C7D] transition-transform ${open ? '' : '-rotate-90'}`} />
          <span className="min-w-0">
            <span className="block text-sm font-medium text-white">{title}</span>
            {!open && summary ? <span className="block text-xs text-[#8696A9] truncate">{summary}</span> : null}
          </span>
        </button>
        {right}
      </div>
      {open && <div className="px-3.5 pb-3.5 pt-0.5">{children}</div>}
    </div>
  );
};

const toList = (v) => v.split('\n').map(x => x.trim()).filter(Boolean);

// Text mit Stift. `flat`: ohne Kasten, mit Trennlinie (für Karten). `list`: Wert ist eine Liste, Anzeige als Aufzählung.
export const EditableText = ({ label, value, onChange, rows = 2, testId, flat = false, list = false, hint }) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const asText = list ? (value || []).join('\n') : (value || '');
  const [draft, setDraft] = useState(asText);
  useEffect(() => { if (!editing) setDraft(asText); }, [asText, editing]);
  const empty = !String(asText).trim();
  const frame = flat ? 'py-3 border-b border-[#1A2B42] last:border-b-0' : 'bg-[#1A2B42] rounded-lg';
  const pad = flat ? 'flex items-start gap-2' : 'flex items-start gap-2 px-3.5 py-3';
  const confirm = () => { onChange(list ? toList(draft) : draft); setEditing(false); setOpen(true); };

  return (
    <div className={frame} data-testid={testId}>
      <div className={pad}>
        <button type="button" onClick={() => !editing && !flat && setOpen(o => !o)} className="flex-1 min-w-0 text-left" aria-expanded={open || editing}>
          <span className="block text-[11px] uppercase tracking-wide text-[#6B7C90] mb-0.5">{label}</span>
          {editing ? null : empty ? (
            <span className="block text-sm text-[#5A6C7D]">{hint || '—'}</span>
          ) : list ? (
            <ul className="list-disc pl-4 space-y-0.5 text-sm text-[#E6EDF5]">{(value || []).map((x, i) => <li key={i}>{x}</li>)}</ul>
          ) : (
            <span className="block text-sm text-[#E6EDF5] whitespace-pre-wrap" style={flat || open ? undefined : clamp2}>{value}</span>
          )}
        </button>
        {editing ? (
          <button type="button" onClick={confirm} className="text-[#00D9C8] p-1" aria-label="OK" data-testid={`${testId}-confirm`}><Check className="w-4 h-4" /></button>
        ) : (
          <button type="button" onClick={() => { setEditing(true); setOpen(true); }} className="text-[#5A6C7D] hover:text-white p-1" aria-label="Edit" data-testid={`${testId}-edit`}><Pencil className="w-3.5 h-3.5" /></button>
        )}
      </div>
      {editing && (
        <div className={flat ? 'mt-1.5' : 'px-3.5 pb-3.5 -mt-1'}>
          <AutoGrow autoFocus value={draft} onChange={e => setDraft(e.target.value)} style={{ minHeight: `${rows * 1.4}rem` }} className="bg-[#0A1628] border-[#2A3B52] text-white text-sm" />
          {list && <p className="text-[11px] text-[#6B7C90] mt-1">Ein Eintrag pro Zeile</p>}
        </div>
      )}
    </div>
  );
};

// Kurze Begriffe (Skills, Sprachen, Branchen) als Chips: Enter oder Komma fügt hinzu.
export const ChipsField = ({ label, values = [], onChange, placeholder, testId }) => {
  const [v, setV] = useState('');
  const add = () => {
    const parts = v.split(/[,\n]/).map(x => x.trim()).filter(Boolean).filter(x => !values.includes(x));
    if (parts.length) onChange([...values, ...parts]);
    setV('');
  };
  return (
    <div className="py-3 border-b border-[#1A2B42] last:border-b-0" data-testid={testId}>
      <span className="block text-[11px] uppercase tracking-wide text-[#6B7C90] mb-1.5">{label}</span>
      <div className="flex flex-wrap gap-1.5">
        {values.map(x => (
          <span key={x} className="inline-flex items-center gap-1 rounded-full bg-[#1A2B42] pl-2.5 pr-1.5 py-1 text-xs text-white">
            {x}<button type="button" onClick={() => onChange(values.filter(y => y !== x))} className="text-[#5A6C7D] hover:text-red-400" aria-label="Remove"><Minus className="w-3 h-3" /></button>
          </span>
        ))}
        <input value={v} onChange={e => setV(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); } }} onBlur={add} placeholder={placeholder} className="min-w-[8rem] flex-1 bg-transparent text-xs text-white placeholder:text-[#5A6C7D] focus:outline-none py-1" data-testid={testId ? `${testId}-input` : undefined} />
      </div>
    </div>
  );
};

// Einheitliche Karte für die Bereiche der Personal DNA.
export const DnaCard = ({ title, hint, right, children, testId }) => (
  <section className="rounded-xl border border-[#1A2B42] bg-[#0F1D32] p-4 sm:p-5" data-testid={testId}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-white">{title}</h3>
        {hint && <p className="text-xs text-[#8696A9] mt-0.5">{hint}</p>}
      </div>
      {right}
    </div>
    <div className="mt-2">{children}</div>
  </section>
);

// Ein Thema: zugeklappt nur der Titel, aufgeklappt Titel + Sicht darauf bearbeiten.
const TopicRow = ({ topic, index, onChange, onRemove, lang }) => {
  const T = (de, en) => (lang === 'de' ? de : en);
  const [open, setOpen] = useState(!topic.title);
  return (
    <div className="rounded-lg bg-[#0A1628]" data-testid={`pb-topic-${index}`}>
      <div className="flex items-center gap-2 px-3 py-2.5">
        <button type="button" onClick={() => setOpen(o => !o)} className="flex-1 min-w-0 flex items-center gap-2 text-left" aria-expanded={open}>
          <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 text-[#5A6C7D] transition-transform ${open ? '' : '-rotate-90'}`} />
          <span className={`text-sm ${topic.title ? 'text-white' : 'text-[#5A6C7D]'} ${open ? '' : 'truncate'}`}>{topic.title || T('Neues Thema', 'New topic')}</span>
        </button>
        <button type="button" onClick={onRemove} className="text-[#5A6C7D] hover:text-red-400 p-1" aria-label="Remove" data-testid={`pb-topic-remove-${index}`}><Minus className="w-4 h-4" /></button>
      </div>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          <AutoGrow value={topic.title} onChange={e => onChange({ title: e.target.value })} placeholder={T('Thema', 'Topic')} className="bg-[#1A2B42] border-[#2A3B52] text-white text-sm font-medium" data-testid={`pb-topic-title-${index}`} />
          <AutoGrow value={topic.description} onChange={e => onChange({ description: e.target.value })} placeholder={T('Deine Sicht darauf', 'Your point of view')} className="bg-[#1A2B42] border-[#2A3B52] text-[#A0AEC0] text-xs" />
        </div>
      )}
    </div>
  );
};

// Eine Karrierestation: zugeklappt Rolle + Firma, aufgeklappt alle Felder.
const CareerRow = ({ item, index, onChange, onRemove, lang }) => {
  const T = (de, en) => (lang === 'de' ? de : en);
  const [open, setOpen] = useState(!item.role && !item.company);
  const title = [item.role, item.company].filter(Boolean).join(' · ');
  return (
    <div className="rounded-lg bg-[#0A1628]" data-testid={`pb-career-${index}`}>
      <div className="flex items-center gap-2 px-3 py-2.5">
        <button type="button" onClick={() => setOpen(o => !o)} className="flex-1 min-w-0 flex items-center gap-2 text-left" aria-expanded={open}>
          <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 text-[#5A6C7D] transition-transform ${open ? '' : '-rotate-90'}`} />
          <span className={`text-sm ${title ? 'text-white' : 'text-[#5A6C7D]'} ${open ? '' : 'truncate'}`}>{title || T('Neue Station', 'New position')}</span>
          {!open && item.period ? <span className="text-xs text-[#6B7C90] flex-shrink-0">{item.period}</span> : null}
        </button>
        <button type="button" onClick={onRemove} className="text-[#5A6C7D] hover:text-red-400 p-1" aria-label="Remove" data-testid={`pb-career-remove-${index}`}><Minus className="w-4 h-4" /></button>
      </div>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          <AutoGrow value={item.role} onChange={e => onChange({ role: e.target.value })} placeholder={T('Rolle', 'Role')} className="bg-[#1A2B42] border-[#2A3B52] text-white text-sm font-medium" data-testid={`pb-career-role-${index}`} />
          <AutoGrow value={item.company} onChange={e => onChange({ company: e.target.value })} placeholder={T('Unternehmen', 'Company')} className="bg-[#1A2B42] border-[#2A3B52] text-white text-sm" />
          <AutoGrow value={item.period} onChange={e => onChange({ period: e.target.value })} placeholder={T('Zeitraum, z. B. 2019 – heute', 'Period, e.g. 2019 – present')} className="bg-[#1A2B42] border-[#2A3B52] text-[#A0AEC0] text-xs" />
          <AutoGrow value={item.description} onChange={e => onChange({ description: e.target.value })} placeholder={T('Was du dort gemacht hast', 'What you did there')} className="bg-[#1A2B42] border-[#2A3B52] text-[#A0AEC0] text-xs" />
        </div>
      )}
    </div>
  );
};

// Parallele Rollen (z. B. Geschäftsführer von zwei Firmen): je Rolle eigene Strategie und Beiträge.
const RoleRow = ({ item, index, onChange, onRemove, lang, onAddWebsite }) => {
  const T = (de, en) => (lang === 'de' ? de : en);
  const [site, setSite] = useState('');
  const [adding, setAdding] = useState(false);
  const sources = item.sources || [];
  const addSite = async () => {
    if (!site.trim() || !onAddWebsite || adding) return;
    setAdding(true);
    try {
      const src = await onAddWebsite(item.id, site.trim());
      if (src) { onChange({ sources: [...sources, src] }); setSite(''); }
    } finally { setAdding(false); }
  };
  return (
    <div className="rounded-lg border border-[#1A2B42] bg-[#0A1628] p-3.5" data-testid={`pb-role-${index}`}>
      <p className="text-[11px] uppercase tracking-wide text-[#6B7C90] mb-2">{T('Beschäftigung', 'Job')} {index + 1}</p>
      <div className="flex items-start gap-2">
        <div className="flex-1 space-y-2.5">
          <AutoGrow value={item.job} onChange={e => onChange({ job: e.target.value })} placeholder={T('Rolle, z. B. Geschäftsführerin', 'Role, e.g. managing director')} className="bg-[#1A2B42] border-[#2A3B52] text-white text-sm font-medium" data-testid={`pb-role-job-${index}`} />
          <AutoGrow value={item.company} onChange={e => onChange({ company: e.target.value })} placeholder={T('Unternehmen', 'Company')} className="bg-[#1A2B42] border-[#2A3B52] text-white text-sm" data-testid={`pb-role-company-${index}`} />
          <AutoGrow value={item.description} onChange={e => onChange({ description: e.target.value })} placeholder={T('Was diese Rolle umfasst (Branche, Aufgaben, Zielgruppe)', 'What this role covers (industry, tasks, audience)')} className="bg-[#1A2B42] border-[#2A3B52] text-[#A0AEC0] text-xs" />
          {sources.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-0.5" data-testid={`pb-role-sources-${index}`}>
              {sources.map((s, si) => (
                <span key={si} className="inline-flex items-center gap-1 rounded-full bg-[#1A2B42] px-2.5 py-1 text-xs text-white" title={s.url || s.label}>
                  {s.type === 'project' ? '▣' : '🌐'} {s.label || s.url}
                  <button type="button" onClick={() => onChange({ sources: sources.filter((_, k) => k !== si) })} className="text-[#5A6C7D] hover:text-red-400" aria-label="Remove source"><Minus className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}
          {onAddWebsite && item.id ? (
            <div className="flex gap-2">
              <input value={site} onChange={e => setSite(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSite(); } }} placeholder={T('Website hinzufügen, z. B. www.firma.de', 'Add a website, e.g. www.company.com')} className="flex-1 rounded-lg border bg-[#1A2B42] border-[#2A3B52] px-3 py-1.5 text-white text-xs focus:outline-none focus:ring-1 focus:ring-[#00D9C8]" data-testid={`pb-role-site-${index}`} />
              <button type="button" onClick={addSite} disabled={!site.trim() || adding} className="rounded-lg bg-[#1A2B42] hover:bg-[#2A3B52] text-white px-3 text-xs disabled:opacity-50" data-testid={`pb-role-site-add-${index}`}>{adding ? '…' : <Plus className="w-3.5 h-3.5" />}</button>
            </div>
          ) : (!item.id && onAddWebsite ? <p className="text-[11px] text-[#6B7C90]">{T('Speichere das Profil, dann kannst du Websites hinzufügen.', 'Save the profile first, then you can add websites.')}</p> : null)}
        </div>
        <button type="button" onClick={onRemove} className="text-[#5A6C7D] hover:text-red-400 p-1" aria-label="Remove" data-testid={`pb-role-remove-${index}`}><Minus className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

const GENDERS = [
  { id: 'female', de: 'Weiblich', en: 'Female' },
  { id: 'male', de: 'Männlich', en: 'Male' },
  { id: 'diverse', de: 'Divers', en: 'Diverse' },
  { id: '', de: 'Keine Angabe', en: 'Prefer not to say' },
];

const GoalPills = ({ value, onChange, lang }) => (
  <div className="py-3 border-b border-[#1A2B42]" data-testid="pb-main-goal">
    <span className="block text-[11px] uppercase tracking-wide text-[#6B7C90] mb-1.5">{lang === 'de' ? 'Hauptziel' : 'Main goal'}</span>
    <div className="flex flex-wrap gap-1.5">
      {PERSONAL_GOALS.map(g => (
        <button type="button" key={g.id} onClick={() => onChange(g.id)} className={`rounded-full border px-3 py-1 text-xs transition-colors ${(value || 'thought_leadership') === g.id ? 'border-[#00D9C8] bg-[#00D9C8]/15 text-white' : 'border-[#2A3B52] text-[#A0AEC0] hover:text-white hover:border-[#00D9C8]/40'}`} data-testid={`pb-goal-${g.id}`}>
          {lang === 'de' ? g.de : g.en}
        </button>
      ))}
    </div>
  </div>
);

/**
 * Tab „Du“: wofür du stehst, Themen, Haltung & Material, Angaben zu dir.
 */
export const DnaProfileTab = ({ profile, onChange, lang = 'de' }) => {
  const T = (de, en) => (lang === 'de' ? de : en);
  const set = (key, val) => onChange({ ...profile, [key]: val });
  const topics = profile.topics || [];
  const setTopic = (i, patch) => set('topics', topics.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));

  return (
    <div className="space-y-4" data-testid="dna-tab-profile">
      <DnaCard title={T('Wofür du stehst', 'What you stand for')} hint={T('Der Kern deiner Marke. Daraus entstehen Positionierung und Ideen.', 'The core of your brand. Positioning and ideas are built from it.')} testId="dna-card-stand">
        <EditableText flat label={T('Positionierung', 'Positioning')} value={profile.positioning} onChange={v => set('positioning', v)} rows={3} hint={T('Wofür willst du bekannt sein? Ein Satz.', 'What do you want to be known for? One sentence.')} testId="pb-review-positioning" />
        <EditableText flat label={T('Dein Schwerpunkt', 'Your focus')} value={profile.expertise} onChange={v => set('expertise', v)} hint={T('Wo bist du Expertin oder Experte?', 'Where are you an expert?')} testId="profile-expertise" />
        <EditableText flat label={T('Zielgruppe', 'Audience')} value={profile.audience} onChange={v => set('audience', v)} rows={3} hint={T('Wen willst du erreichen, und was beschäftigt diese Menschen?', 'Who do you want to reach, and what concerns them?')} testId="pb-review-audience" />
        <EditableText flat label={T('Ziel', 'Goal')} value={profile.goal} onChange={v => set('goal', v)} rows={2} testId="pb-review-goal" />
        <GoalPills value={profile.main_goal} onChange={v => set('main_goal', v)} lang={lang} />
      </DnaCard>

      <DnaCard
        title={`${T('Themen', 'Topics')} (${topics.length})`} hint={T('Worüber du regelmäßig schreibst, mit deiner Sicht darauf.', 'What you write about regularly, with your point of view.')} testId="pb-review-topics"
        right={topics.length < 8 ? <button type="button" onClick={() => set('topics', [...topics, { title: '', description: '' }])} className="text-[#00D9C8] p-1 shrink-0" aria-label="Add topic" data-testid="pb-topic-add"><Plus className="w-4 h-4" /></button> : null}
      >
        <div className="space-y-1.5">
          {topics.map((t, i) => <TopicRow key={i} topic={t} index={i} lang={lang} onChange={patch => setTopic(i, patch)} onRemove={() => set('topics', topics.filter((_, idx) => idx !== i))} />)}
          {topics.length === 0 && <p className="text-xs text-[#5A6C7D]">{T('Noch keine Themen. Mit + hinzufügen.', 'No topics yet. Add one with +.')}</p>}
        </div>
      </DnaCard>

      <DnaCard title={T('Haltung & Material', 'Convictions & material')} hint={T('Was du glaubst und woraus deine Beiträge entstehen. Je konkreter, desto besser die Texte.', 'What you believe and what your posts are made of. The more concrete, the better the texts.')} testId="pb-review-more">
        <EditableText flat list label={T('Haltungen & Überzeugungen', 'Convictions & beliefs')} value={profile.convictions} onChange={v => set('convictions', v)} rows={3} hint={T('Wovon bist du überzeugt, was siehst du anders als andere?', 'What are you convinced of, what do you see differently?')} testId="pb-review-convictions" />
        <EditableText flat list label={T('Geschichten, die du erzählen könntest', 'Stories you could tell')} value={profile.story_angles} onChange={v => set('story_angles', v)} rows={3} testId="pb-review-stories" />
        <EditableText flat list label={T('Belege & Erfahrungen', 'Proof & experience')} value={profile.proof_points} onChange={v => set('proof_points', v)} rows={3} hint={T('Zahlen, Projekte, Ergebnisse, auf die du dich stützen kannst.', 'Numbers, projects, results you can draw on.')} testId="pb-review-proof" />
        <EditableText flat list label={T('Worüber du nicht sprechen willst', 'Topics to avoid')} value={profile.avoid} onChange={v => set('avoid', v)} rows={2} testId="pb-review-avoid" />
      </DnaCard>

      <DnaCard title={T('Zu dir', 'About you')} hint={T('Damit Texte in der richtigen Form für dich geschrieben werden.', 'So texts are written in the right form for you.')} testId="pb-review-details">
        <div className="py-3 border-b border-[#1A2B42]" data-testid="pb-review-gender">
          <span className="block text-[11px] uppercase tracking-wide text-[#6B7C90] mb-1.5">{T('Geschlecht', 'Gender')}</span>
          <div className="flex flex-wrap gap-1.5">
            {GENDERS.map(g => (
              <button type="button" key={g.id} onClick={() => set('gender', g.id)} className={`rounded-full border px-3 py-1 text-xs transition-colors ${(profile.gender || '') === g.id ? 'border-[#00D9C8] bg-[#00D9C8]/15 text-white' : 'border-[#2A3B52] text-[#A0AEC0] hover:text-white hover:border-[#00D9C8]/40'}`} data-testid={`pb-gender-${g.id || 'none'}`}>
                {lang === 'de' ? g.de : g.en}
              </button>
            ))}
          </div>
        </div>
        <EditableText flat label={T('Standort', 'Location')} value={profile.location} onChange={v => set('location', v)} testId="pb-review-location" />
        <EditableText flat label={T('In deinen Worten', 'In your own words')} value={profile.about} onChange={v => set('about', v)} rows={4} hint={T('Was dich antreibt, was du erreichen willst …', 'What drives you, what you want to achieve …')} testId="pb-review-about" />
      </DnaCard>
    </div>
  );
};

/**
 * Tab „Werdegang & Firmen“: Lebenslauf & Quellen, Beschäftigungen mit ihren Quellen, Stationen, Fähigkeiten & Erfolge.
 * `cvSlot` ist der Upload-Bereich (Lebenslauf / LinkedIn-Export) samt gelesenen Dateien.
 */
export const DnaCareerTab = ({ profile, onChange, lang = 'de', onAddRoleWebsite, cvSlot }) => {
  const T = (de, en) => (lang === 'de' ? de : en);
  const set = (key, val) => onChange({ ...profile, [key]: val });
  const roles = profile.roles || [];
  const setRole = (i, patch) => set('roles', roles.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const career = profile.career || [];
  const setCareer = (i, patch) => set('career', career.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));

  return (
    <div className="space-y-4" data-testid="dna-tab-career">
      {cvSlot && (
        <DnaCard title={T('Lebenslauf & Quellen', 'CV & sources')} hint={T('Lade deinen Lebenslauf oder LinkedIn-Export hoch. Wir ergänzen dein Profil, nichts wird überschrieben.', 'Upload your CV or LinkedIn export. We add to your profile, nothing is overwritten.')} testId="section-cv-update">
          {cvSlot}
        </DnaCard>
      )}

      <DnaCard
        title={`${T('Beschäftigungen & Firmen', 'Jobs & companies')} (${roles.length})`}
        hint={roles.length >= 2 ? T('Du hast mehrere Beschäftigungen: Jede bekommt eigene Strategie und Ideen, dazu gibt es kombinierte.', 'You have several jobs: each gets its own strategy and ideas, plus combined ones.') : T('Wo du arbeitest, mit Websites und Projekten als Hintergrund. Bei mehreren gleichzeitigen Jobs trägst du jeden ein.', 'Where you work, with websites and projects as background. With several simultaneous jobs, add each one.')}
        testId="pb-review-roles"
        right={roles.length < 4 ? <button type="button" onClick={() => set('roles', [...roles, { id: '', job: '', company: '', description: '' }])} className="text-[#00D9C8] p-1 shrink-0" aria-label="Add role" data-testid="pb-role-add"><Plus className="w-4 h-4" /></button> : null}
      >
        <div className="space-y-3">
          {roles.length === 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="current-position">
              <input value={profile.job || ''} onChange={e => set('job', e.target.value)} placeholder={T('Aktuelle Position', 'Current position')} className="rounded-lg border bg-[#1A2B42] border-[#2A3B52] px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-[#00D9C8]" data-testid="profile-job" />
              <input value={profile.company || ''} onChange={e => set('company', e.target.value)} placeholder={T('Unternehmen', 'Company')} className="rounded-lg border bg-[#1A2B42] border-[#2A3B52] px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-[#00D9C8]" />
            </div>
          )}
          {roles.map((r, i) => (
            <RoleRow key={r.id || i} item={r} index={i} lang={lang} onAddWebsite={onAddRoleWebsite} onChange={patch => setRole(i, patch)} onRemove={() => set('roles', roles.filter((_, idx) => idx !== i))} />
          ))}
          {roles.length === 1 && <p className="text-xs text-[#6B7C90]">{T('Mit nur einer Beschäftigung werden Strategie und Ideen nicht getrennt. Ihre Quellen fließen trotzdem als Hintergrund ein.', 'With a single job, strategy and ideas are not separated. Its sources still serve as background.')}</p>}
        </div>
        <EditableText flat label={T('Dein Bereich / deine Abteilung', 'Your area / department')} value={profile.work_about} onChange={v => set('work_about', v)} rows={3} hint={T('Was machst du konkret, für wen arbeitet ihr?', 'What do you do concretely, who do you work for?')} testId="pb-review-work-about" />
      </DnaCard>

      <DnaCard
        title={`${T('Werdegang', 'Career')} (${career.length})`} testId="pb-review-background"
        right={career.length < 12 ? <button type="button" onClick={() => set('career', [...career, { role: '', company: '', period: '', description: '' }])} className="text-[#00D9C8] p-1 shrink-0" aria-label="Add position" data-testid="pb-career-add"><Plus className="w-4 h-4" /></button> : null}
      >
        <EditableText flat label={T('Über dich', 'About you')} value={profile.cv_summary} onChange={v => set('cv_summary', v)} rows={3} testId="pb-review-cv-summary" />
        <div className="space-y-1.5 py-3" data-testid="pb-career-list">
          {career.map((c, i) => <CareerRow key={i} item={c} index={i} lang={lang} onChange={patch => setCareer(i, patch)} onRemove={() => set('career', career.filter((_, idx) => idx !== i))} />)}
          {career.length === 0 && <p className="text-xs text-[#5A6C7D]">{T('Noch keine Stationen. Lade deinen Lebenslauf hoch oder füge mit + eine hinzu.', 'No positions yet. Upload your CV or add one with +.')}</p>}
        </div>
        <EditableText flat list label={T('Ausbildung', 'Education')} value={profile.education} onChange={v => set('education', v)} rows={2} testId="pb-review-education" />
      </DnaCard>

      <DnaCard title={T('Fähigkeiten & Erfolge', 'Skills & achievements')} testId="pb-review-skills-card">
        <ChipsField label={T('Skills', 'Skills')} values={profile.skills || []} onChange={v => set('skills', v)} placeholder={T('Skill hinzufügen …', 'Add a skill …')} testId="pb-review-skills" />
        <ChipsField label={T('Sprachen', 'Languages')} values={profile.languages || []} onChange={v => set('languages', v)} placeholder={T('Sprache hinzufügen …', 'Add a language …')} testId="pb-review-languages" />
        <ChipsField label={T('Branchen', 'Industries')} values={profile.industries || []} onChange={v => set('industries', v)} placeholder={T('Branche hinzufügen …', 'Add an industry …')} testId="pb-review-industries" />
        <EditableText flat list label={T('Erfolge & Projekte', 'Achievements & projects')} value={profile.achievements} onChange={v => set('achievements', v)} rows={3} testId="pb-review-achievements" />
      </DnaCard>
    </div>
  );
};
